package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

data class LBreadcrumbItem(
    val label: String,
    val path: String? = null,
)

/**
 * The trail, at the size the shell spec sets it: `body/15`, not `small/13.5`.
 *
 * `app-shell.md` gives the crumb as "`$font-body` | 15 / 600" and records the geometry it rejected
 * as a numbered defect — "Trail crumb padding 4/8 -> 28px tall | Visual 32 tall, hit area 40 tall".
 * What shipped was exactly the rejected version: 13.5px at weight 400 in a 4px-padded box, which
 * measures 28.25 tall. Both halves are fixed here and on [LBreadcrumbLinkStyle].
 */
val LBreadcrumbsStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.BodySmall)
        .lineHeight(TypeScale.BodyLineHeight)
}

val LBreadcrumbsListStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .flexWrap(FlexWrap.Wrap)
        .gap(Space.XS)
        .margin(0.px)
        .padding(0.px)
        .styleModifier { property("list-style", "none") }
}

val LBreadcrumbItemStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "inline-flex") }
        .alignItems(AlignItems.Center)
        .gap(Space.XS)
}

/**
 * A crumb is a target, so it gets the target floor.
 *
 * The 40px comes from `minHeight` plus centring rather than from padding, because padding tall
 * enough to reach 40 would also make the crumb visually 40 — and the spec asks for a 32-tall
 * *visual* inside a 40-tall *hit area*, which is the same split [LButtonStyle] and
 * [LIconButtonHitAreaStyle] draw for short buttons. The horizontal padding moves 4 -> 8 so the
 * focus ring has somewhere to sit around the word.
 *
 * `check-control-geometry.mjs` will not flag a regression here: it excludes `<a>` deliberately,
 * on the grounds that an inline link inside a sentence is governed by line-height rather than by a
 * hit area. That is right for prose and wrong for a crumb, and the exclusion is why this one went
 * unmeasured.
 */
val LBreadcrumbLinkStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textSecondary)
            .borderRadius(Radius.Control)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .padding(leftRight = Space.SM)
            .fontWeight(600)
            .whiteSpace(WhiteSpace.NoWrap)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
            }
            .transitionColors()
    }
    hover { Modifier.color(colorMode.toSitePalette().accent) }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/** The last crumb is not a link, but it shares the row, so it shares the box. */
val LBreadcrumbCurrentStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        .borderRadius(Radius.Control)
        .minHeight(HIT_AREA_FLOOR_PX.px)
        .padding(leftRight = Space.SM)
        .fontWeight(600)
        .whiteSpace(WhiteSpace.NoWrap)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
        }
}

val LBreadcrumbSeparatorStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "inline-flex") }
        .color(colorMode.toSitePalette().textMuted)
        .iconSize(IconSize.SM)
}

@Composable
fun LBreadcrumbs(
    items: List<LBreadcrumbItem>,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lbreadcrumbs.breadcrumb", "Breadcrumb"),
) {
    if (items.isEmpty()) return

    Nav(
        LBreadcrumbsStyle.toModifier().then(modifier).toAttrs {
            attr("aria-label", label)
        },
    ) {
        Ol(LBreadcrumbsListStyle.toModifier().toAttrs()) {
            items.forEachIndexed { index, item ->
                val isCurrent = index == items.lastIndex
                Li(LBreadcrumbItemStyle.toModifier().toAttrs()) {
                    if (isCurrent) {
                        Span(LBreadcrumbCurrentStyle.toModifier().toAttrs { attr("aria-current", "page") }) {
                            Text(item.label)
                        }
                    } else if (!item.path.isNullOrBlank()) {
                        Link(
                            path = item.path,
                            modifier = LBreadcrumbLinkStyle.toModifier(),
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        ) {
                            Text(item.label)
                        }
                    } else {
                        Span(LBreadcrumbLinkStyle.toModifier().toAttrs()) {
                            Text(item.label)
                        }
                    }
                    // The chevron trails the crumb it belongs to rather than leading the one that
                    // follows it. The list wraps between `Li`s, so a separator drawn at the head of
                    // the child carried the glyph down with it and the second line of a wrapped
                    // trail opened with a bare `›` — which reads as a bullet, not as a
                    // relationship. Rendered here, the chevron stays on the parent's line and a
                    // wrap always begins with a word. Same count either way: one per non-last crumb.
                    if (!isCurrent) {
                        Span(LBreadcrumbSeparatorStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                            LChevronRightIcon(Modifier.iconSize(IconSize.SM).attr("data-directional", "inline"))
                        }
                    }
                }
            }
        }
    }
}
